import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

import type { Metric } from 'webapp/models-custom';

interface Args {
	model: Metric | null;
	onOpenExtraInfoPopup?: () => void;
}

export default class MetricLibraryMetricInfoComponent extends Component<Args> {
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	get showAboutLink() {
		return this.args.model?.description && !this.isScriptOrQueryMetric;
	}

	get isScriptOrQueryMetric() {
		return (
			this.args.model?.isTypeMetricCalculationGeneric ||
			this.args.model?.isTypeMetricCalculationOrGeneric
		);
	}

	get source() {
		return this.args.model?.source;
	}

	@action
	openExtraInfoPopup() {
		this.args.onOpenExtraInfoPopup?.();

		if (this.args.model) {
			this.mmMetricsService.openExtraInfoPopup(this.args.model);
		}
	}
	<template>
		<div class="block-metric-info base-info">
			<div class="sources">
				{{#if this.source}}
					<div class="single-source">
						<img src={{@model.sourceAvatar}} alt />
						{{@model.sourceName}}
					</div>
				{{/if}}
			</div>

			<div class="name">
				<strong>{{@model.name}}</strong>
				{{#if @model.isTypeMetricCalculationOrGeneric}}
					<span class="metric-type-icon">
						<CommonUiIconComponent @name="Calculator" @size="sm" />
						<ToolTipComponent @position="top" @text="This is a calculated metric" />
					</span>
				{{else if @model.isTypeCustomQueryOrGeneric}}
					<span class="metric-type-icon">
						<CommonUiIconComponent @name="Data" @size="sm" />
						<ToolTipComponent @position="top" @text="This is a built metric" />
					</span>
					{{#if @model.allowDimensions}}
						<CommonUiIconComponent @name="Breakdown" @size="sm" />
						<ToolTipComponent
							@position="top"
							@text="Breakdown by dimension(s) available."
						/>
					{{/if}}
				{{/if}}
				<VerificationBadgeComponent
					@verification={{@model.verificationInfo}}
					@entityLabel="metric"
					@size="sm"
				/>
				{{#if @model.isFavorite}}
					<CommonUiIconComponent @name="StarFill" @size="sm" />
				{{/if}}
				{{#if @model.isNew}}
					<div class="new-feature-tag">New</div>
				{{/if}}
			</div>
		</div>
		<div class="block-metric-info">
			{{#if @model.description}}
				<div class="info">
					<p class="desc">{{@model.description}}</p>
					{{#if this.showAboutLink}}
						<div class="more-info">
							<a
								role="button"
								class="more-info-link"
								{{on "click" this.openExtraInfoPopup}}
							>
								<CommonUiIconComponent @name="Book" @size="sm" />
								More about this metric
							</a>
						</div>
					{{/if}}
				</div>
			{{/if}}
		</div>
	</template>
}
